import { forwardRef, useMemo } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";

import { IsMobileAtom } from "../../../../App";
import { sidebarOpenAtom } from "../../../../atoms/SidebarAtoms";
import { callFunctionIfExists } from "../../../../utils/GeneralUtils";
import { modulesAtom } from "../../../../atoms/GlobalAtoms";
import { Module } from "../../../../types/TenantInterfaces";

interface Props {
  selectedRecords: any[];
  convertDisabled?: boolean;
  module?: Module | null;
  onClear: () => void;
  setExportDrawer: (bool: boolean) => void;
  setAddToListDrawer?: (bool: boolean) => void;
  toggleBulkApplyTags: () => void;
  toggleUpdateRecordsModal: () => void;
  toggleMergeDrawer: () => void;
  toggleConvertDrawer?: () => void;
  deleteSelectedRecords: () => void;
  handleDuplicateRecord: () => void;
  toggleFeedbackTransformDrawer?: () => void;
  disabledSidebarCheck?: boolean;
}

const SharedSelectedRecordsActions = ({
  selectedRecords,
  convertDisabled,
  module,
  onClear,
  setExportDrawer,
  setAddToListDrawer,
  toggleUpdateRecordsModal,
  toggleBulkApplyTags,
  toggleMergeDrawer,
  toggleConvertDrawer,
  deleteSelectedRecords,
  handleDuplicateRecord,
  toggleFeedbackTransformDrawer,
  disabledSidebarCheck = false,
}: Props) => {
  const modules = useAtomValue(modulesAtom);
  const length = selectedRecords?.length;
  const isMobile = useAtomValue(IsMobileAtom);

  // Check if there's at least 1 module configured as List
  const someModuleList = useMemo(() => {
    return modules?.some((module) => module?.ModuleList?.IsModuleList);
  }, [modules]);

  return (
    <FloatingToolbar disableSidebarCheck={disabledSidebarCheck}>
      <ActionsCloseButton onClear={onClear} length={length} />
      <div
        className="d-flex align-items-center ml-2"
        style={{ overflowX: "auto" }}
      >
        {setAddToListDrawer && someModuleList && (
          <IconAction
            onClick={() => setAddToListDrawer(true)}
            icon="plus-circle"
            title="Add To List"
            isMobile={true}
          />
        )}
        {setExportDrawer && (
          <IconAction
            onClick={() => setExportDrawer(true)}
            icon="file-export"
            title="Export"
            isMobile={isMobile}
          />
        )}
        {toggleUpdateRecordsModal && (
          <IconAction
            onClick={toggleUpdateRecordsModal}
            icon="pencil"
            title="Update"
            isMobile={isMobile}
          />
        )}
        {toggleBulkApplyTags && (
          <IconAction
            onClick={toggleBulkApplyTags}
            icon="tag-multiple"
            title="Tags"
            isMobile={isMobile}
          />
        )}
        {deleteSelectedRecords && (
          <IconAction
            onClick={deleteSelectedRecords}
            icon="delete"
            title="Delete"
            isMobile={isMobile}
          />
        )}
        {length === 1 && handleDuplicateRecord && (
          <IconAction
            onClick={handleDuplicateRecord}
            icon="content-copy"
            title="Duplicate"
            isMobile={isMobile}
          />
        )}
        {length === 2 && toggleMergeDrawer && (
          <IconAction
            onClick={toggleMergeDrawer}
            icon="merge"
            title="Merge"
            isMobile={isMobile}
          />
        )}
        {toggleConvertDrawer && module?.APIName !== "Feedback" && (
          <IconAction
            icon="account-convert-outline"
            title="Convert"
            isMobile={isMobile}
            onClick={() => {
              callFunctionIfExists(toggleConvertDrawer);
            }}
          />
        )}
        {module && module.APIName === "Feedback" && (
          <IconAction
            icon="comment-text-outline"
            title="Transform into Issue"
            isMobile={isMobile}
            onClick={() => {
              callFunctionIfExists(toggleFeedbackTransformDrawer);
            }}
          />
        )}
      </div>
    </FloatingToolbar>
  );
};

export const ActionsCloseButton = ({
  onClear,
  length,
}: {
  onClear: () => void;
  length: number;
}) => {
  const isMobile = useAtomValue(IsMobileAtom);

  return (
    <div className="text-white text-center mb-0 d-flex align-items-center font-weight-bold">
      <div
        style={{ height: 62, paddingLeft: 14 }}
        className={classNames(
          "d-flex align-items-center cursor-pointer bg-medium rounded-left",
          { "mr-3": !isMobile }
        )}
        onClick={onClear}
      >
        <i
          className={`mdi mdi-close mr-2 ml--1`}
          style={{ fontSize: 26, lineHeight: 1 }}
        />
      </div>
      {!isMobile && <div className="text-nowrap">{length} Selected</div>}
    </div>
  );
};

export default SharedSelectedRecordsActions;

export function IconAction({
  onClick,
  icon,
  title,
  isMobile,
}: {
  onClick: () => void;
  icon: string;
  title?: string;
  isMobile?: boolean;
}) {
  const iconSize = isMobile ? 18 : 24;
  const textSize = isMobile ? 12 : 14;

  return (
    <div
      onClick={onClick}
      className="d-flex flex-column align-items-center px-2 py-1 mx-1 my-1 bg-medium--hover cursor-pointer rounded justify-content-center w-100 text-nowrap"
    >
      <i
        className={`mdi mdi-${icon}`}
        style={{ fontSize: iconSize, lineHeight: 1 }}
      />
      <span
        className="font-weight-500"
        style={{ fontSize: textSize, textAlign: "center", lineHeight: "13px" }}
      >
        {title}
      </span>
    </div>
  );
}

export const FloatingToolbar = forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    bottom?: number;
    minWidth?: number;
    className?: string;
    disableSidebarCheck?: boolean;
  }
>(
  (
    {
      children,
      bottom = 80,
      minWidth = 350,
      className = "bg-primary",
      /**
       * We use a default width base on the sidebar if this is opened or not.
       * Setting this value to true will fix some style issues when using the floating toolbar
       * inside of a drawer.
       */
      disableSidebarCheck = false,
    },
    ref
  ) => {
    const isMobile = useAtomValue(IsMobileAtom);
    const sidebarOpen = useAtomValue(sidebarOpenAtom);
    const sidebarWidth = disableSidebarCheck ? 0 : sidebarOpen ? 125 : 30;

    return (
      <div
        ref={ref}
        style={{
          zIndex: 9,
          position: "fixed",
          bottom: bottom,
          left: isMobile ? "50%" : `calc(50% + ${sidebarWidth}px)`,
          transform: "translateX(-50%)",
          maxWidth: "100vw",
          width: "auto",
          minWidth: minWidth,
        }}
        className={`border d-flex justify-content-between align-items-center text-white rounded btn-oauth ${className} ${
          minWidth ? "pr-2" : ""
        }`}
      >
        {children}
      </div>
    );
  }
);
